import { resolveTransferTimeLabel } from '@utils'
import cn from 'classnames'
import { getPaymentOptionName } from 'mmfintech-backend-api'
import { useEffect, useRef, useState } from 'react'
import PaymentOptionIcon from '../../PaymentOptionIcon'
import type { PaymentMethodOption } from '../PaymentMethodSelect'
import styles from './PaymentMethodItem.module.scss'

const OVERFLOW_SLIDE_BUFFER_PX = 24

type PaymentMethodItemProps = {
  method: PaymentMethodOption
  variant: 'list' | 'collapsed'
  onClick: () => void
  selected?: boolean
  disabled?: boolean
  fullWidth?: boolean
  className?: string
}

function PaymentMethodItem(props: PaymentMethodItemProps) {
  const { method, variant, onClick, selected, disabled, fullWidth, className } = props

  // const { paymentMethod, feePercent, feeFlat, feeFlatCurrency, description, title, dataTest } = method

  const titleRef = useRef<HTMLSpanElement>(null)
  const subtitleRef = useRef<HTMLSpanElement>(null)

  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    applyOverflowSlide(titleRef.current, hovered)
    applyOverflowSlide(subtitleRef.current, hovered)
  }, [hovered])

  const isCollapsed = variant === 'collapsed'
  const resolvedTitle = method?.title || getPaymentOptionName(method?.paymentMethod)
  // The only subtitle shown is the transfer time; nothing renders when it isn't available.
  const transferTimeLabel = resolveTransferTimeLabel(method)

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-test={method?.dataTest}
      className={cn(styles['payment-method-item'], 'payment-method-wrapper', className, {
        'full-width': fullWidth || isCollapsed,
        selected: isCollapsed || selected,
        disabled
      })}
    >
      <div className={styles['payment-method-icon-wrapper']}>
        <PaymentOptionIcon paymentOption={method?.paymentMethod} />
      </div>
      <div className={styles['payment-method-content']}>
        <div className={styles['payment-method-title-wrapper']}>
          <span ref={titleRef}>{resolvedTitle}</span>
        </div>
        {transferTimeLabel ? (
          <span ref={subtitleRef} className='subtitle'>
            {transferTimeLabel}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function applyOverflowSlide(el: HTMLSpanElement | null, active: boolean) {
  if (!el?.parentElement) return
  if (!active) {
    el.style.transform = 'translateX(0px)'
    return
  }
  const parent = el.parentElement
  const overflows = el.clientWidth > parent.clientWidth || el.scrollWidth > el.clientWidth
  if (!overflows) return
  const translateX = Math.max(
    el.clientWidth - parent.clientWidth + OVERFLOW_SLIDE_BUFFER_PX,
    el.scrollWidth - el.clientWidth
  )
  el.style.transform = `translateX(${-translateX}px)`
}

export default PaymentMethodItem
